import React from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowRight, Info, X } from "@phosphor-icons/react";
import { MappedCptCodes } from "api/insurancePlanCptCodes";
import OverrideChip from "./OverrideChip";

export interface HoursAddendumFormState {
  clinicalJustification: string;
  hours: Record<string, number>;
}

export const INITIAL_HOURS_ADDENDUM_FORM: HoursAddendumFormState = {
  clinicalJustification: "",
  hours: {},
};

const SLOT_ORDER: (keyof MappedCptCodes)[] = [
  "assessment",
  "directTherapy",
  "caregiverMediated",
  "protocolModificationDirect",
  "protocolModificationIndirect",
  "caregiverTraining",
];

export const getUniqueCodesFromMapping = (
  mappedCptCodes: MappedCptCodes | null
): string[] => {
  if (!mappedCptCodes) return [];
  const codes: string[] = [];
  for (const slot of SLOT_ORDER) {
    const c = mappedCptCodes[slot]?.code;
    if (c && !codes.includes(c)) codes.push(c);
  }
  return codes;
};

export const isHoursAddendumFormValid = (
  form: HoursAddendumFormState,
  seeded: HoursAddendumFormState
): boolean => {
  if (!form.clinicalJustification.trim()) return false;
  const allCodes = new Set([
    ...Object.keys(form.hours),
    ...Object.keys(seeded.hours),
  ]);
  for (const code of allCodes) {
    if ((form.hours[code] ?? 0) !== (seeded.hours[code] ?? 0)) return true;
  }
  return false;
};

export interface HoursRequestEntry {
  code: string;
  originalHours: number;
  requestedHours: number;
}

export interface HoursAddendumPayload {
  clinicalJustification: string;
  hoursRequest: HoursRequestEntry[];
}

export const buildHoursAddendumPayload = (
  form: HoursAddendumFormState,
  seeded: HoursAddendumFormState,
  mappedCptCodes: MappedCptCodes | null
): HoursAddendumPayload => {
  const codes = getUniqueCodesFromMapping(mappedCptCodes);
  return {
    clinicalJustification: form.clinicalJustification,
    hoursRequest: codes.map((code) => ({
      code,
      originalHours: Number(seeded.hours[code]) || 0,
      requestedHours: Number(form.hours[code]) || 0,
    })),
  };
};

interface HoursAddendumStepProps {
  /** Whether an active override waives this document's readiness checks */
  isOverridden?: boolean;
  form: HoursAddendumFormState;
  seeded: HoursAddendumFormState;
  onClinicalJustificationChange: (value: string) => void;
  onHoursChange: (code: string, value: number) => void;
  clientId: string;
  clientName: string;
  mappedCptCodes: MappedCptCodes | null;
  isLoading: boolean;
  onClose: () => void;
  onContinue: () => void;
}

interface NumberFieldProps {
  label: string;
  helperText: string;
  value: number;
  onChange: (v: number) => void;
}

const NumberField: React.FC<NumberFieldProps> = ({ label, helperText, value, onChange }) => (
  <TextField
    label={label}
    helperText={helperText}
    fullWidth
    type="number"
    value={value}
    onChange={(e) => onChange(Number(e.target.value) || 0)}
    inputProps={{ min: 0, step: 0.25 }}
  />
);

const HoursAddendumStep: React.FC<HoursAddendumStepProps> = ({
  form,
  seeded,
  onClinicalJustificationChange,
  onHoursChange,
  clientId,
  clientName,
  mappedCptCodes,
  isLoading,
  onClose,
  onContinue,
  isOverridden = false,
}) => {
  const goToInsurance = () => {
    window.open(`/clients/${clientId}/insurance`, "_blank", "noopener,noreferrer");
  };

  const codes = getUniqueCodesFromMapping(mappedCptCodes);

  if (isLoading) {
    return (
      <Stack alignItems="center" sx={{ py: 11 }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 7,
          py: 5,
        }}
      >
        <Box>
          <Typography variant="h4" component="h2">
            Hours Addendum Request
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Request additional authorized hours to address changes in {clientName}&apos;s clinical
            needs.
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="Close dialog"
          size="small"
          sx={{ color: "text.secondary" }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 7, pb: 7, pt: 0 }}>
        <Stack sx={{ mt: 7, gap: 5 }}>
          {isOverridden && <OverrideChip />}
          <TextField
            label="Clinical justification"
            required
            fullWidth
            multiline
            minRows={4}
            value={form.clinicalJustification}
            onChange={(e) => onClinicalJustificationChange(e.target.value)}
            helperText="Provide justification for the hours addendum request"
          />

          {codes.map((code) => (
            <NumberField
              key={code}
              label={code}
              helperText="Hours per authorization period"
              value={form.hours[code] ?? 0}
              onChange={(v) => onHoursChange(code, v)}
            />
          ))}

          <Alert
            severity="info"
            icon={<Info size={20} />}
            action={
              <Button
                variant="text"
                size="small"
                onClick={goToInsurance}
                sx={{
                  // TODO: replace with palette token once pkg-theme exposes info.infoContent
                  color: "#014361",
                  whiteSpace: "nowrap",
                  bgcolor: "transparent",
                  "&:hover": { bgcolor: "transparent" },
                }}
              >
                Go To Insurance
              </Button>
            }
          >
            <AlertTitle>Payor Requirements</AlertTitle>
            Each payor defines which CPT codes they approve for ABA, determining the services
            available to clients under their insurance plans. If a service has no approved CPT code,
            we cannot request hours for that service.
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 7, pt: 5, pb: 5, gap: 2 }}>
        <Button variant="contained" size="medium" color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          endIcon={<ArrowRight size={16} />}
          disabled={!isHoursAddendumFormValid(form, seeded) && !isOverridden}
          onClick={onContinue}
        >
          Continue To Goals
        </Button>
      </DialogActions>
    </>
  );
};

export default HoursAddendumStep;
